import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { interval, Observable, of, throwError, timer } from 'rxjs';
import { catchError, delay, map, switchMap, take } from 'rxjs/operators';
import { fakeStream } from '../common/sse';
import { DemoFailures } from '../common/demo-failures';
import {
  DEMO_DELAY, SSE_MULT, PREVIEW_LOAD_DELAY,
  FORCE_ALL_NOT_MAPPED, FORCE_ALL_REVIEWED,
} from '../../constants/demo.config';
import {
  AppService, Profile, HumanReviewQueue, UploadResult, UploadDirListing, UploadFilePreview,
  ImageFolderList, ImageSummaryData, ImageGifList, StreamStatus,
  ValidationRow, ReviewVariableData, ExecutionPreviewData, ExecutionPreviewQuery, ExecutionSummary,
  TransformChatMessage, TransformSuggestion, TransformationOption,
  TargetReloadData, TransformReloadData, PipelineStats, ValidationSummary,
  PagedResult, AuditVariable, ProfileVariableRow, Session, PlanQueryOpts, ProfileQueryOpts,
  ProfileVariableEditDefaults, ProfileVariableEditPatch,
  ColumnValuesQuery, ColumnValuesData, ExecutionFailures,
} from './app.service';

@Injectable()
export class DemoService extends AppService {
  private readonly http = inject(HttpClient);

  override setRunName(_name: string): void {}

  // Timing and failure switches live in shared/constants/demo.config.ts, shared
  // with the omics demo so one edit tunes the whole demo.
  private readonly failures = inject(DemoFailures);
  protected failOnce(key: string): boolean { return this.failures.once(key); }
  protected shouldFail(key: string): boolean { return this.failures.should(key); }

  // ── Upload Module ─────────────────────────────────────────

  uploadFile(file: File, card: string, slot: number, serverPath?: string): Promise<UploadResult> {
    // The real backend registers a file under its path relative to the files root
    // and echoes that back as `saved_name`, so the demo does the same — the card
    // labels and the restored-session files both read it.
    const savedName = serverPath ?? file.name;
    console.log(`[Demo] File uploaded: "${savedName}" (card: ${card}, slot: ${slot})`);
    if (this.shouldFail('uploadFile')) return Promise.reject({ message: 'Simulated upload error' });
    // if (this.failOnce('uploadFile')) return Promise.reject({ message: 'Simulated upload error' }); // ← uncomment to test individually
    return Promise.resolve({ ok: true, savedName });
  }

  /**
   * Fixture name for one folder's listing, or one file's full preview.
   *
   * The demo imitates the real API's *lazy* contract rather than faking it out of a
   * single file: one fixture per folder, fetched only when that folder is opened,
   * and one fixture per file preview, fetched only for the file the user selects.
   * Assets are flat, so a path is slugified into a filename
   * (`Studies/Cohort_A` → `Studies_Cohort_A`); the root listing is `root`.
   */
  private static fixtureSlug(path: string): string {
    return path ? path.replace(/[^A-Za-z0-9_-]+/g, '_') : 'root';
  }

  browseUploadDir(path: string): Observable<UploadDirListing> {
    if (this.shouldFail('browseUploadDir')) return throwError(() => ({ message: 'Simulated error: could not load file list' }));
    // if (this.failOnce('browseUploadDir')) return throwError(() => ({ message: 'Simulated error: could not load file list' })); // ← uncomment to test individually
    return this.http.get<UploadDirListing>(
      `assets/demo/tabular/upload_files/${DemoService.fixtureSlug(path)}.json`,
    ).pipe(delay(DEMO_DELAY));
  }

  getUploadFilePreview(path: string): Observable<UploadFilePreview> {
    if (this.shouldFail('getUploadFilePreview')) return throwError(() => ({ message: 'Simulated error: could not load preview' }));
    // if (this.failOnce('getUploadFilePreview')) return throwError(() => ({ message: 'Simulated error: could not load preview' })); // ← uncomment to test individually
    // PREVIEW_LOAD_DELAY rather than DEMO_DELAY: previews are the one place a
    // visible wait is wanted, and this is now a real per-file round trip.
    return this.http.get<UploadFilePreview>(
      `assets/demo/tabular/upload_previews/${DemoService.fixtureSlug(path)}.json`,
    ).pipe(delay(PREVIEW_LOAD_DELAY));
  }

  getUploadFilePreviewUrl(path: string): string {
    const filename = path.split('/').pop()!;
    return `assets/demo/tabular/upload/${encodeURIComponent(filename)}`;
  }

  getPreviewLoadDelay(): number { return PREVIEW_LOAD_DELAY; }
  override getPdfPreviewShouldFail(): boolean { return this.shouldFail('pdfPreview'); }

  watchUploadStatus(): Observable<StreamStatus> {
    if (this.shouldFail('watchUploadStatus')) return throwError(() => ({ message: 'Simulated error: profiling start failed' }));
    // if (this.failOnce('watchUploadStatus')) return throwError(() => ({ message: 'Simulated error: profiling start failed' })); // ← uncomment to test individually
    const messages = [
      'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris.',
      'Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit.',
    ];
    return fakeStream(messages, 1000 * SSE_MULT, 800 * SSE_MULT);
  }

  reconnectUploadStatus(): Observable<StreamStatus> { return this.watchUploadStatus(); }

  getProfiles(): Observable<Record<string, Profile>> {
    if (this.shouldFail('dataProfilingData')) return throwError(() => ({ message: 'Simulated error: could not load profiling data' }));
    // if (this.failOnce('getProfiles')) return throwError(() => ({ message: 'Simulated error: could not load profiling data' })); // ← uncomment to test individually
    return this.http.get<Record<string, Profile>>('assets/demo/tabular/profiles_metadata.json').pipe(
      delay(DEMO_DELAY),
    );
  }

  watchProfilingStatus(): Observable<StreamStatus> {
    if (this.shouldFail('watchProfilingStatus')) return throwError(() => ({ message: 'Simulated error: profiling pipeline failed' }));
    // if (this.failOnce('watchProfilingStatus')) return throwError(() => ({ message: 'Simulated error: profiling pipeline failed' })); // ← uncomment to test individually
    const messages = [
      'Sed ut perspiciatis unde omnis iste natus error sit voluptatem accusantium doloremque laudantium, totam rem aperiam eaque ipsa quae ab illo inventore veritatis et quasi architecto beatae vitae dicta.',
      'Nemo enim ipsam voluptatem quia voluptas sit aspernatur aut odit aut fugit, sed quia consequuntur magni dolores eos qui ratione voluptatem sequi nesciunt neque porro quisquam est.',
    ];
    return fakeStream(messages, 1000 * SSE_MULT, 800 * SSE_MULT);
  }

  reconnectProfilingStatus(): Observable<StreamStatus> { return this.watchProfilingStatus(); }

  watchPlanningStatus(): Observable<StreamStatus> {
    if (this.shouldFail('watchPlanningStatus')) return throwError(() => ({ message: 'Simulated error: planning pipeline failed' }));
    // if (this.failOnce('watchPlanningStatus')) return throwError(() => ({ message: 'Simulated error: planning pipeline failed' })); // ← uncomment to test individually
    const messages = [
      'At vero eos et accusamus et iusto odio dignissimos ducimus qui blanditiis praesentium voluptatum deleniti atque corrupti quos dolores et quas molestias excepturi sint occaecati cupiditate non provident.',
      'Nam libero tempore cum soluta nobis est eligendi optio cumque nihil impedit quo minus id quod maxime placeat facere possimus, omnis voluptas assumenda est, omnis dolor repellendus itaque earum rerum.',
    ];
    return fakeStream(messages, 1000 * SSE_MULT, 800 * SSE_MULT);
  }

  reconnectPlanningStatus(): Observable<StreamStatus> { return this.watchPlanningStatus(); }

  // ── Strategy & Planning Module ────────────────────────────

  getHumanReviewQueue(threshold: number | null, unreviewed = false): Observable<HumanReviewQueue> {
    if (this.shouldFail('reviewPageLoad')) return throwError(() => ({ message: 'Simulated error: could not load review queue' }));
    type Row = { id: string; decision_confidence: number; target_confidence: number; transform_confidence: number };
    return this.http.get<{ variables: Row[] }>('assets/demo/tabular/execution_plan_confidences.json').pipe(
      delay(300),
      map(data => {
        const ids = data.variables
          .filter(v => {
            if (unreviewed) return !this.isReviewed(v.id);   // un-reviewed queue: only not-yet-reviewed
            const avg = (v.decision_confidence + v.target_confidence + v.transform_confidence) / 3;
            return threshold === null || (avg <= threshold && avg !== 1);
          })
          .map(v => v.id);
        return { ids, total: ids.length };
      }),
    );
  }

  checkAllNotMapped(): Observable<{ all_not_mapped: boolean }> {
    if (this.shouldFail('checkAllNotMapped')) return throwError(() => ({ message: 'Simulated error: could not check variable mapping status' }));
    return of({ all_not_mapped: FORCE_ALL_NOT_MAPPED }).pipe(delay(DEMO_DELAY));
  }

  saveSteps(variableId: string, steps: unknown[]): void {
    console.log(`[Demo] saveSteps — variable: "${variableId}"`, steps);
  }

  getReviewData(decisionId: string): Observable<ReviewVariableData> {
    if (this.shouldFail('reviewPageLoad')) return throwError(() => ({ message: 'Simulated error: could not load review data' }));
    return this.http.get<ReviewVariableData>(`assets/demo/tabular/review/${decisionId}.json`).pipe(
      delay(DEMO_DELAY),
      // Reflect reviewed status (drives the "Already reviewed" badge) from the same
      // in-memory set the plan table uses, so the whole demo stays consistent.
      map(data => ({ ...data, source_info: { ...data.source_info, reviewed: this.isReviewed(decisionId) } })),
    );
  }

  startDecisionChange(_decisionId: string, _decision: string, _mappingFrom?: string[], _selectedTargetId?: string): Observable<{ ok: boolean }> {
    if (this.shouldFail('decisionChangeFlow')) return throwError(() => ({ message: 'Simulated error: decision change request failed' }));
    // if (this.failOnce('startDecisionChange')) return throwError(() => ({ message: 'Simulated error: decision change request failed' })); // ← uncomment to test individually
    return of({ ok: true });
  }

  getDecisionTargets(_decisionId: string, decision: string): Observable<TargetReloadData> {
    if (this.shouldFail('decisionChangeFlow')) return throwError(() => ({ message: 'Simulated error: could not load decision targets' }));
    // if (this.failOnce('getDecisionTargets')) return throwError(() => ({ message: 'Simulated error: could not load decision targets' })); // ← uncomment to test individually
    return this.http.get<TargetReloadData>('assets/demo/tabular/review/decision_reload.json').pipe(
      // Every decision with a Sources step gets the plan's original picks, so the
      // "AHA's pick" badge and the priority ordering work the same in all three. The real
      // backend only fills this for calculated_variable today (adapters._norm_variable);
      // demo shows the intended contract, not that gap.
      map(data => decision === 'not_mapped' || decision === 'matched'
        ? data
        : { ...data, mapping_from_system_selected: ['GDS_1', 'GDS_7'] }
      ),
    );
  }

  startTransformReload(_decisionId: string, _decision: string, _targetId: string, _mappingFrom?: string[]): Observable<{ ok: boolean }> {
    if (this.shouldFail('transformReloadFlow')) return throwError(() => ({ message: 'Simulated error: transform reload request failed' }));
    // if (this.failOnce('startTransformReload')) return throwError(() => ({ message: 'Simulated error: transform reload request failed' })); // ← uncomment to test individually
    return of({ ok: true });
  }

  getTargetTransforms(_decisionId: string, _targetId: string): Observable<TransformReloadData> {
    if (this.shouldFail('transformReloadFlow')) return throwError(() => ({ message: 'Simulated error: could not load transforms' }));
    // if (this.failOnce('getTargetTransforms')) return throwError(() => ({ message: 'Simulated error: could not load transforms' })); // ← uncomment to test individually
    return this.http.get<TransformReloadData>('assets/demo/tabular/review/transform_reload.json');
  }

  // Pre-seeded so the un-reviewed button / "Already reviewed" badge are testable on a
  // fresh demo load (these ids show as reviewed in the table, are excluded from the
  // un-reviewed queue, and show the badge in the wizard). markVariableReviewed adds more.
  private readonly reviewedKeys = new Set<string>(['var_002', 'var_003', 'var_006']);

  // Single source of truth for "is this variable reviewed?" in demo — used by the plan
  // table, the un-reviewed queue and the review badge. `_forceAllReviewed` overrides all.
  private isReviewed(id: string): boolean {
    return FORCE_ALL_REVIEWED || this.reviewedKeys.has(id);
  }

  markVariableReviewed(
    decisionId: string,
    _decision: string,
    _targetId: string | null,
    _selectedTransformation: unknown,
    _mappingFrom?: string[],
  ): Observable<void> {
    this.reviewedKeys.add(decisionId);
    return of(undefined);
  }

  splitVariable(_decisionId: string): Observable<{ created_variable_ids: string[] }> {
    return of({ created_variable_ids: [] });
  }

  suggestTransformation(
    _decisionId: string,
    _decision: string,
    _targetId: string,
    _selectedTransformation: unknown,
    _userMessage: string,
    _history: TransformChatMessage[],
  ): Observable<TransformSuggestion> {
    if (this.shouldFail('suggestTransformation')) return throwError(() => ({ message: 'Simulated error: chat pipeline failed' }));
    return timer(500).pipe(
      switchMap(() => this.http.get<TransformSuggestion>('assets/demo/tabular/transform_suggestions.json'))
    );
  }

  private customCounter = 0;

  // Simulates the backend applying + validating the code and returning the FULL card
  // (id, title, description, confidence, preview, notes, code). shouldFail → error path.
  private demoTransform(code: Record<string, unknown>[], id: string): TransformationOption {
    return {
      id,
      rank: -1,
      title: 'Custom transformation',
      description: 'Built from your steps and applied to the data.',
      confidence: 0.9,
      system_selected: false,
      ai_suggested: false,
      notes: 'Your custom steps were applied to the source variable. This preview shows how sample values flow through the transformation — review it and confirm it matches what you expect before selecting it.',
      preview: [
        { original: 'example_in_1', new_value: 'example_out_1' },
        { original: 'example_in_2', new_value: 'example_out_2' },
      ],
      code,
    };
  }

  createTransform(_decisionId: string, _targetId: string, code: Record<string, unknown>[]): Observable<TransformationOption> {
    if (this.shouldFail('regenerateTransform')) return throwError(() => ({ message: 'The transformation could not be applied. Check the steps and try again.' }));
    return timer(700).pipe(map(() => this.demoTransform(code, `tf_custom_${++this.customCounter}`)));
  }

  editTransform(_decisionId: string, transformationId: string, code: Record<string, unknown>[]): Observable<TransformationOption> {
    if (this.shouldFail('regenerateTransform')) return throwError(() => ({ message: 'The transformation could not be applied. Check the steps and try again.' }));
    return timer(700).pipe(map(() => this.demoTransform(code, transformationId)));   // keep the same id
  }

  // ── Execution Module ──────────────────────────────────────

  startExecution(): Observable<{ ok: boolean; session_id: string }> {
    return of({ ok: true, session_id: 'demo-session' });
  }

  watchExecutionStatus(): Observable<StreamStatus> {
    if (this.shouldFail('watchExecutionStatus')) return throwError(() => ({ message: 'Simulated error: execution pipeline failed' }));
    const messages = [
      'Temporibus autem quibusdam et aut officiis debitis rerum necessitatibus saepe eveniet ut et voluptates repudiandae sint molestiae non recusandae itaque earum rerum hic tenetur a sapiente delectus.',
      'Quis autem vel eum iure reprehenderit qui in ea voluptate velit esse quam nihil molestiae consequatur vel illum qui dolorem eum fugiat quo voluptas nulla pariatur.',
    ];
    return fakeStream(messages, 1000 * SSE_MULT, 800 * SSE_MULT);
  }

  reconnectExecutionStatus(): Observable<StreamStatus> { return this.watchExecutionStatus(); }

  watchDecisionChangeStatus(_decisionId: string, _decision: string): Observable<StreamStatus> {
    const messages = [
      'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua ut enim ad minim veniam quis nostrud exercitation.',
      'Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur excepteur sint occaecat cupidatat non proident deserunt mollit anim.',
      'Ut labore et dolore magnam aliquam quaerat voluptatem nemo enim ipsam voluptatem quia voluptas sit aspernatur aut odit aut fugit sed quia consequuntur magni dolores eos.',
    ];
    return fakeStream(messages, 900 * SSE_MULT, 600 * SSE_MULT);
  }

  watchTransformReloadStatus(_decisionId: string, _decision: string, _targetId: string): Observable<StreamStatus> {
    const messages = [
      'Sed ut perspiciatis unde omnis iste natus error sit voluptatem accusantium doloremque laudantium totam rem aperiam eaque ipsa quae ab illo inventore veritatis et quasi architecto.',
      'At vero eos et accusamus et iusto odio dignissimos ducimus qui blanditiis praesentium voluptatum deleniti atque corrupti quos dolores et quas molestias excepturi sint occaecati.',
      'Nam libero tempore cum soluta nobis est eligendi optio cumque nihil impedit quo minus id quod maxime placeat facere possimus omnis voluptas assumenda est omnis dolor repellendus.',
    ];
    return fakeStream(messages, 900 * SSE_MULT, 600 * SSE_MULT);
  }

  searchValidationResults(q: string, page: number, pageSize: number, _sortBy?: string): Observable<PagedResult<ValidationRow>> {
    if (this.shouldFail('getValidationResults')) return throwError(() => ({ message: 'Simulated error: could not load validation results' }));
    const term = q.toLowerCase();
    return this.http.get<PagedResult<ValidationRow>>(`assets/demo/tabular/validation/ps${pageSize}/p0.json`).pipe(
      delay(DEMO_DELAY),
      map((result: PagedResult<ValidationRow>) => {
        const filtered = result.data.filter(v =>
          String(v.variable ?? '').toLowerCase().includes(term) ||
          String(v.concept ?? '').toLowerCase().includes(term)
        );
        const start = page * pageSize;
        return { data: filtered.slice(start, start + pageSize), total: filtered.length, page, page_size: pageSize };
      }),
    );
  }

  searchProfileVariables(dataset: string, q: string, page: number, pageSize: number, opts?: ProfileQueryOpts): Observable<PagedResult<ProfileVariableRow>> {
    if (this.shouldFail('dataProfilingData')) return throwError(() => ({ message: 'Simulated error: could not load profiling variables' }));
    const term = q.toLowerCase();
    // Search + filter + sort all span the whole dataset, so load the full set (ps100/p0).
    return this.http.get<PagedResult<ProfileVariableRow>>(`assets/demo/tabular/profile_variables/${dataset}/ps100/p0.json`).pipe(
      delay(DEMO_DELAY),
      map((result: PagedResult<ProfileVariableRow>) => {
        let rows = this.applyProfileVariableEdits(dataset, result.data).filter(v =>
          String(v.variable_name ?? '').toLowerCase().includes(term) ||
          String(v.description ?? '').toLowerCase().includes(term) ||
          String(v.data_type ?? '').toLowerCase().includes(term)
        );
        rows = this.applyProfileTypeFilter(rows, opts?.filters);
        rows = this.applyProfileSort(rows, opts?.sortBy);
        if (opts?.priorityIds?.length) rows = this.orderByPriority(rows, opts.priorityIds);
        const start = page * pageSize;
        return { data: rows.slice(start, start + pageSize), total: rows.length, page, page_size: pageSize };
      }),
    );
  }

  getValidationResults(page: number, pageSize: number, _sortBy?: string): Observable<PagedResult<ValidationRow>> {
    if (this.shouldFail('getValidationResults')) return throwError(() => ({ message: 'Simulated error: could not load validation results' }));
    // if (this.failOnce('getValidationResults')) return throwError(() => ({ message: 'Simulated error: could not load validation results' })); // ← uncomment to test individually
    return this.http.get<PagedResult<ValidationRow>>(`assets/demo/tabular/validation/ps${pageSize}/p${page}.json`).pipe(
      delay(DEMO_DELAY),
    );
  }

  getValidationSummary(): Observable<ValidationSummary> {
    if (this.shouldFail('getValidationSummary')) return throwError(() => ({ message: 'Simulated error: could not load validation summary' }));
    return this.http.get<ValidationSummary>('assets/demo/tabular/validation_summary.json').pipe(delay(DEMO_DELAY));
  }

  notifyReplan(_failedVariables: string[]): Observable<void> {
    return of(undefined);
  }

  getPipelineStats(): Observable<PipelineStats> {
    if (this.shouldFail('getPipelineStats')) return throwError(() => ({ message: 'Simulated error: could not load pipeline stats' }));
    return this.http.get<PipelineStats>('assets/demo/tabular/pipeline_stats.json').pipe(delay(DEMO_DELAY));
  }

  getExecutionSummary(): Observable<ExecutionSummary> {
    if (this.shouldFail('getExecutionSummary')) return throwError(() => ({ message: 'Simulated error: could not load the execution summary' }));
    // Served as-is. The fixture already carries what the endpoint is documented to return,
    // percentages included, so demo mode exercises the same payload the real one will send
    // rather than a version this service patched on the way through.
    return this.http.get<ExecutionSummary>('assets/demo/tabular/execution_summary.json')
      .pipe(delay(DEMO_DELAY));
  }

  // Served as-is: the endpoint takes no filter, so neither does the fixture.
  getExecutionFailures(): Observable<ExecutionFailures> {
    if (this.shouldFail('getExecutionFailures')) return throwError(() => ({ message: 'Simulated error: could not load the failed variables' }));
    return this.http.get<ExecutionFailures>('assets/demo/tabular/execution_failures.json')
      .pipe(delay(DEMO_DELAY));
  }

  // Stands in for the server: the fixture is the unfiltered page, and everything the query
  // asks for is applied here, so the component receives data already narrowed — the same
  // shape the real endpoint will return.
  /** Low on purpose: the fixture's columns have a handful of values each, and a realistic
   *  cap would never trip. At 6 the demo shows both a complete funnel and a cut one, which
   *  is the case the "show all" path exists for. */
  private readonly DEMO_FILTER_VALUES_LIMIT = 6;

  getExecutionPreview(q: ExecutionPreviewQuery): Observable<ExecutionPreviewData> {
    if (this.shouldFail('getExecutionPreview')) return throwError(() => ({ message: 'Simulated error: could not load execution preview' }));
    return this.http.get<ExecutionPreviewData>(`assets/demo/tabular/execution_preview/ps${q.page_size}/p${q.page}.json`).pipe(
      map(d => this.applyPreviewQuery(d, q)),
      delay(DEMO_DELAY),
    );
  }

  /** The uncapped list for one column, faceted like the preview's: every other column's
   *  filter applies, this column's own is lifted. Reads the same fixture the preview does,
   *  so the two always agree about what a column holds. */
  getColumnValues(q: ColumnValuesQuery): Observable<ColumnValuesData> {
    if (this.shouldFail('getColumnValues')) {
      return throwError(() => ({ message: 'Simulated error: could not load the column values' }));
    }
    return this.http.get<ExecutionPreviewData>('assets/demo/tabular/execution_preview/ps100/p0.json').pipe(
      map(d => {
        const side = q.side === 'harmonized' ? 'harmonized' : 'raw';
        const rows = (side === 'harmonized' ? d.harmonized.rows : d.raw.rows) ?? [];
        const other = Object.entries(q.column_filters ?? {})
          .filter(([key]) => key !== `${side}:${q.column}`);
        const counts = new Map<string, number>();
        rows.forEach((r, i) => {
          for (const [key, picks] of other) {
            const [s2, col] = key.split(':');
            const src = s2 === 'raw' ? d.raw.rows?.[i] : d.harmonized.rows?.[i];
            if (!picks.includes(String(src?.[col] ?? ''))) return;
          }
          const v = String(r[q.column] ?? '');
          counts.set(v, (counts.get(v) ?? 0) + 1);
        });
        return {
          side: q.side,
          column: q.column,
          values: [...counts.entries()]
            .map(([value, count]) => ({ value, count }))
            .sort((a, b) => b.count - a.count || a.value.localeCompare(b.value, undefined, { numeric: true })),
        };
      }),
      delay(DEMO_DELAY),
    );
  }

  /** What the backend will do: narrow the columns to one decision, drop the harmonized side
   *  when that decision produced nothing, then filter rows by search and by column picks. */
  private applyPreviewQuery(d: ExecutionPreviewData, q: ExecutionPreviewQuery): ExecutionPreviewData {
    // The fixture carries a decision per column so this can stand in for the server's
    // filtering. It is not part of the response: the real endpoint filters the columns
    // itself, and the frontend never needs to know which decision produced which column.
    const decs = (d as unknown as { column_decisions?: (string | null)[] }).column_decisions ?? [];
    let rawCols = d.raw.columns;
    let harmCols = d.harmonized.columns;

    if (q.decision && decs.length) {
      const keep = d.raw.columns.map((_, i) => i).filter(i => decs[i] === q.decision);
      rawCols = keep.map(i => d.raw.columns[i]);
      // not_mapped produced no target columns at all; showing any would be a mapping that
      // was never made.
      harmCols = q.decision === 'not_mapped' ? [] : keep.map(i => d.harmonized.columns[i]);
    }

    // The search narrows COLUMNS, not rows: the funnels already filter by value, per column
    // and with counts, and finding one column among fifty is the harder problem here. A
    // column is kept when either side's name matches, together with its counterpart — the
    // two panes are only worth reading side by side.
    const term = (q.search ?? '').trim().toLowerCase();
    if (term) {
      // `search_in` picks which side is matched; the counterpart comes along either way, so
      // scoping changes what has to match, never whether the pair stays together. Omitted is
      // bidirectional, which is what a reviewer who does not know how a name was harmonized
      // needs.
      const hitsRaw  = (i: number) => (rawCols[i]  ?? '').toLowerCase().includes(term);
      const hitsHarm = (i: number) => (harmCols[i] ?? '').toLowerCase().includes(term);
      const matches =
        q.search_in === 'harmonized'   ? hitsHarm :
        q.search_in === 'unharmonized' ? hitsRaw  :
        (i: number) => hitsRaw(i) || hitsHarm(i);
      const idx = rawCols.map((_, i) => i).filter(matches);
      rawCols = idx.map(i => rawCols[i]);
      harmCols = harmCols.length ? idx.map(i => harmCols[i]).filter(Boolean) : [];
    }

    const narrow = (rows: Record<string, string>[], cols: string[]): Record<string, string>[] =>
      (rows ?? []).map(r => Object.fromEntries(cols.map(c => [c, r[c]])) as Record<string, string>);

    const rawAll = narrow(d.raw.rows ?? [], rawCols);
    const harmAll = harmCols.length ? narrow(d.harmonized.rows ?? [], harmCols) : [];

    // Rows are kept or dropped on both sides together, so the comparison stays aligned.
    const kept = rawAll.map((_, i) => i).filter(i => {
      for (const [key, picks] of Object.entries(q.column_filters ?? {})) {
        const [side, col] = key.split(':');
        const row = side === 'raw' ? rawAll[i] : harmAll[i];
        if (!picks.includes(String(row?.[col] ?? ''))) return false;
      }
      return true;
    });

    // Filter values cascade, the way a spreadsheet's do: each column is counted over the rows that
    // survive every OTHER column's filter, so a value that would return nothing is not
    // offered. A column is measured with its OWN filter lifted — otherwise picking `1`
    // would leave `1` as the only value it lists, and there would be no way back.
    const passes = (i: number, exceptKey: string): boolean => {
      for (const [key, picks] of Object.entries(q.column_filters ?? {})) {
        if (key === exceptKey) continue;
        const [side, col] = key.split(':');
        const row = side === 'raw' ? rawAll[i] : harmAll[i];
        if (!picks.includes(String(row?.[col] ?? ''))) return false;
      }
      return true;
    };

    const tally = (side: string, cols: string[], rows: Record<string, string>[],
                   cut: Record<string, boolean>) => {
      const out: Record<string, { value: string; count: number }[]> = {};
      for (const c of cols) {
        const counts = new Map<string, number>();
        rows.forEach((r, i) => {
          // `${side}:${c}` is the key the REQUEST uses; the response nests by side instead,
          // so nothing downstream has to split a string to find out where a column lives.
          if (!passes(i, `${side}:${c}`)) return;
          const v = String(r[c] ?? '');
          counts.set(v, (counts.get(v) ?? 0) + 1);
        });
        const all = [...counts.entries()]
          .map(([value, count]) => ({ value, count }))
          .sort((a, b) => b.count - a.count || a.value.localeCompare(b.value, undefined, { numeric: true }));
        // Top-N by count, as the server caps it, and the flag says which columns lost values.
        // Without the flag a cut list and a complete one are the same response, and the funnel
        // would present a slice as the whole set.
        cut[c] = all.length > limit;
        out[c] = all.slice(0, limit);
      }
      return out;
    };
    const limit = q.filter_values_limit ?? this.DEMO_FILTER_VALUES_LIMIT;
    const rawCut: Record<string, boolean> = {};
    const harmCut: Record<string, boolean> = {};
    const rawFilterValues = tally('raw', rawCols, rawAll, rawCut);
    const harmFilterValues = tally('harmonized', harmCols, harmAll, harmCut);

    // `column_decisions` is fixture-only — the data this stand-in needs to do the server's
    // filtering. Spreading `d` would put it in the response, where the contract has no such
    // field and the frontend has no use for it.
    const { column_decisions: _drop, ...rest } = d as ExecutionPreviewData & { column_decisions?: unknown };
    return {
      ...rest,
      raw: {
        ...d.raw, columns: rawCols, rows: kept.map(i => rawAll[i]),
        filter_values: rawFilterValues, filter_values_truncated: rawCut,
      },
      harmonized: {
        ...d.harmonized,
        columns: harmCols,
        rows: harmCols.length ? kept.map(i => harmAll[i]).filter(Boolean) : [],
        filter_values: harmFilterValues, filter_values_truncated: harmCut,
      },
    };
  }

  private applyPlanFilter(data: AuditVariable[], opts?: PlanQueryOpts): AuditVariable[] {
    let result = data;

    if (opts?.replanFilter) result = result.filter(v => v.decision === 'not_mapped');

    const filters = opts?.filters ?? [];

    const decisionFilters = filters.filter(f =>
      ['matched', 'mapped', 'partially_mapped', 'calculated_variable', 'not_mapped'].includes(f)
    );
    if (decisionFilters.length) result = result.filter(v => decisionFilters.includes(v.decision));

    const hiFilter = filters.find(f => f === 'intervention_needed' || f === 'intervention_not_needed');
    if (hiFilter && opts?.threshold !== undefined) {
      const t = opts.threshold;
      result = result.filter(v => {
        const vals = [v.decision_confidence, v.target_confidence, v.transform_confidence]
          .filter((x): x is number => x != null);
        const avg = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
        const needed = avg <= t && avg !== 1;
        return hiFilter === 'intervention_needed' ? needed : !needed;
      });
    }

    return result;
  }

  private applySortToVariables(data: AuditVariable[], sortBy?: string): AuditVariable[] {
    if (!sortBy) return data;
    const avg = (v: AuditVariable): number => {
      const vals = [v.decision_confidence, v.target_confidence, v.transform_confidence]
        .filter((x): x is number => x != null);
      return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
    };
    const sorted = [...data];
    switch (sortBy) {
      case 'confidence_desc': return sorted.sort((a, b) => avg(b) - avg(a));
      case 'confidence_asc':  return sorted.sort((a, b) => avg(a) - avg(b));
      case 'reviewed_first':  return sorted.sort((a, b) => (b.reviewed ? 1 : 0) - (a.reviewed ? 1 : 0));
      case 'reviewed_last':   return sorted.sort((a, b) => (a.reviewed ? 1 : 0) - (b.reviewed ? 1 : 0));
      default: return data;
    }
  }

  getPlanVariables(page: number, pageSize: number, opts?: PlanQueryOpts): Observable<PagedResult<AuditVariable>> {
    if (this.shouldFail('getPlanVariables')) return throwError(() => ({ message: 'Simulated error: could not load plan data' }));
    return this.http.get<PagedResult<AuditVariable>>(`assets/demo/tabular/plan_variables/ps${pageSize}/p${page}.json`).pipe(
      delay(DEMO_DELAY),
      map((result: PagedResult<AuditVariable>) => {
        const data = result.data.map(v => this.isReviewed(v.id) ? { ...v, reviewed: true } : v);
        const filtered = this.applyPlanFilter(data, opts);
        const sorted = this.applySortToVariables(filtered, opts?.sortBy);
        return { ...result, data: sorted, total: sorted.length };
      }),
    );
  }

  searchPlanVariables(q: string, page: number, pageSize: number, opts?: PlanQueryOpts): Observable<PagedResult<AuditVariable>> {
    if (this.shouldFail('getPlanVariables')) return throwError(() => ({ message: 'Simulated error: could not load plan data' }));
    const term = q.toLowerCase();
    return this.http.get<PagedResult<AuditVariable>>(`assets/demo/tabular/plan_variables/ps${pageSize}/p0.json`).pipe(
      delay(DEMO_DELAY),
      map((result: PagedResult<AuditVariable>) => {
        let all = result.data.map(v => this.isReviewed(v.id) ? { ...v, reviewed: true } : v);
        all = this.applyPlanFilter(all, opts);
        const filtered = all.filter(v =>
          String(v.mapping_from ?? '').toLowerCase().includes(term) ||
          String(v.mapping_to ?? '').toLowerCase().includes(term) ||
          String(v.decision ?? '').toLowerCase().replace(/_/g, ' ').includes(term)
        );
        const sorted = this.applySortToVariables(filtered, opts?.sortBy);
        const start = page * pageSize;
        return { data: sorted.slice(start, start + pageSize), total: sorted.length, page, page_size: pageSize };
      }),
    );
  }

  getProfileVariables(dataset: string, page: number, pageSize: number, opts?: ProfileQueryOpts): Observable<PagedResult<ProfileVariableRow>> {
    if (this.shouldFail('dataProfilingData')) return throwError(() => ({ message: 'Simulated error: could not load profiling variables' }));
    // Sort / filter / priority all span the whole dataset, so when any is active load
    // the full set (ps100/p0) and paginate in-service. The real backend does this server-side.
    const needsFullSet = !!(opts?.sortBy || opts?.filters?.length || opts?.priorityIds?.length);
    if (needsFullSet) {
      return this.http.get<PagedResult<ProfileVariableRow>>(`assets/demo/tabular/profile_variables/${dataset}/ps100/p0.json`).pipe(
        delay(DEMO_DELAY),
        map(result => {
          let rows = this.applyProfileVariableEdits(dataset, result.data);
          rows = this.applyProfileTypeFilter(rows, opts?.filters);
          rows = this.applyProfileSort(rows, opts?.sortBy);
          if (opts?.priorityIds?.length) rows = this.orderByPriority(rows, opts.priorityIds);
          const start = page * pageSize;
          return { data: rows.slice(start, start + pageSize), total: rows.length, page, page_size: pageSize };
        }),
      );
    }
    return this.http.get<PagedResult<ProfileVariableRow>>(`assets/demo/tabular/profile_variables/${dataset}/ps${pageSize}/p${page}.json`).pipe(
      delay(DEMO_DELAY),
      map(result => {
        const data = this.applyProfileVariableEdits(dataset, result.data);
        return { ...result, data, total: result.total - (result.data.length - data.length) };
      }),
    );
  }

  private static readonly PROFILE_DATA_TYPES = ['int', 'float', 'date', 'binary', 'categorical_nominal', 'categorical_ordinal', 'text'];

  // Data Profiling filter: keep only rows whose data_type is among the selected type filters.
  private applyProfileTypeFilter(rows: ProfileVariableRow[], filters?: string[]): ProfileVariableRow[] {
    const types = (filters ?? []).filter(f => DemoService.PROFILE_DATA_TYPES.includes(f));
    return types.length ? rows.filter(r => types.includes(String(r.data_type ?? ''))) : rows;
  }

  // Data Profiling sort: % missing (numeric) or variable name (alphabetical).
  private applyProfileSort(rows: ProfileVariableRow[], sortBy?: string): ProfileVariableRow[] {
    if (!sortBy) return rows;
    const missing = (r: ProfileVariableRow): number => Number(r.missing_pct ?? 0);
    const name = (r: ProfileVariableRow): string => String(r.variable_name ?? '');
    const sorted = [...rows];
    switch (sortBy) {
      case 'missing_desc': return sorted.sort((a, b) => missing(b) - missing(a));
      case 'missing_asc':  return sorted.sort((a, b) => missing(a) - missing(b));
      case 'name_asc':     return sorted.sort((a, b) => name(a).localeCompare(name(b)));
      case 'name_desc':    return sorted.sort((a, b) => name(b).localeCompare(name(a)));
      default: return rows;
    }
  }

  // Stable reorder: variables whose name is in priorityIds come first, in the exact
  // order of priorityIds; everything else keeps its original relative order.
  private orderByPriority(rows: ProfileVariableRow[], priorityIds: string[]): ProfileVariableRow[] {
    const rank = new Map(priorityIds.map((id, i) => [id, i]));
    const NONE = Number.MAX_SAFE_INTEGER;
    return rows
      .map((row, i) => ({ row, i }))
      .sort((a, b) => {
        const ra = rank.has(a.row.variable_name) ? rank.get(a.row.variable_name)! : NONE;
        const rb = rank.has(b.row.variable_name) ? rank.get(b.row.variable_name)! : NONE;
        return ra !== rb ? ra - rb : a.i - b.i;
      })
      .map(x => x.row);
  }

  // ── Data Profiling: variable edit ──

  private readonly profileVariableEdits = new Map<string, ProfileVariableEditPatch>();
  private readonly deletedProfileVariableKeys = new Set<string>();
  private editKey(dataset: string, variableName: string): string { return `${dataset}::${variableName}`; }

  // Deletions are applied first (a deleted row never shows up, even with a
  // stale patch still sitting in profileVariableEdits from before it was removed).
  private applyProfileVariableEdits(dataset: string, rows: ProfileVariableRow[]): ProfileVariableRow[] {
    return rows
      .filter(v => !this.deletedProfileVariableKeys.has(this.editKey(dataset, v.variable_name)))
      .map(v => {
        const edit = this.profileVariableEdits.get(this.editKey(dataset, v.variable_name));
        return edit ? { ...v, ...edit } : v;
      });
  }

  getProfileVariableEditSuggestions(dataset: string, variableName: string): Observable<ProfileVariableEditDefaults> {
    return this.http.get<ProfileVariableEditDefaults>(`assets/demo/tabular/profile_variable_edit/${dataset}/${encodeURIComponent(variableName)}.json`).pipe(
      delay(DEMO_DELAY),
      // Graceful fallback only — a variable missing its edit-defaults file falls back
      // to the name from the route so the page isn't left blank.
      catchError(() => of({ variable_name: variableName, data_type: '', data_type_system_selected: '', description: '', description_system_selected: '', values: null, values_system_selected: null })),
    );
  }

  updateProfileVariable(dataset: string, variableName: string, patch: ProfileVariableEditPatch): Observable<void> {
    const key = this.editKey(dataset, variableName);
    const merged = { ...(this.profileVariableEdits.get(key) ?? {}), ...patch };
    this.profileVariableEdits.set(key, merged);
    return of(undefined).pipe(delay(DEMO_DELAY));
  }

  deleteProfileVariable(dataset: string, variableName: string): Observable<void> {
    this.deletedProfileVariableKeys.add(this.editKey(dataset, variableName));
    return of(undefined).pipe(delay(DEMO_DELAY));
  }

  // ── Images Module ─────────────────────────────────────────

  listImageFolders(path: string): Observable<ImageFolderList> {
    if (this.shouldFail('listImageFolders')) return throwError(() => ({ message: 'Simulated error: could not load folders' }));
    // if (this.failOnce('listImageFolders')) return throwError(() => ({ message: 'Simulated error: could not load folders' })); // ← uncomment to test individually
    if (path === '') return of({ directories: ['MRIs_Participants_demo'] }).pipe(delay(DEMO_DELAY));
    return of({ directories: [] }).pipe(delay(DEMO_DELAY));
  }

  getImageSummary(_folder: string): Observable<ImageSummaryData> {
    if (this.shouldFail('getImageSummary')) return throwError(() => ({ message: 'Simulated error: could not load image summary' }));
    // if (this.failOnce('getImageSummary')) return throwError(() => ({ message: 'Simulated error: could not load image summary' })); // ← uncomment to test individually
    return this.http.get<ImageSummaryData>('assets/demo/tabular/images_summary.json').pipe(delay(DEMO_DELAY));
  }

  listImageGifs(_folder: string, page: number, pageSize: number): Observable<ImageGifList> {
    if (this.shouldFail('listImageGifs')) return throwError(() => ({ message: 'Simulated error: could not load GIF list' }));
    // if (this.failOnce('listImageGifs')) return throwError(() => ({ message: 'Simulated error: could not load GIF list' })); // ← uncomment to test individually
    return this.http.get<ImageGifList>(`assets/demo/tabular/gifs/ps${pageSize}/p${page}.json`).pipe(
      delay(DEMO_DELAY),
    );
  }

  runPreprocessing(_folder: string): Observable<void> {
    return timer(6000).pipe(map(() => { }));
  }

  watchPreprocessingStatus(_folder: string): Observable<StreamStatus> {
    const messages = [
      '[DEMO] Identifying MRI images...',
      '[DEMO] Running skull stripping...',
      '[DEMO] Reorienting images...',
      '[DEMO] Registering images...',
      '[DEMO] Generating GIF previews...',
    ];
    return interval(1000).pipe(take(messages.length), map(i => ({ message: messages[i] })));
  }

  getGifUrl(path: string): string {
    return `assets/demo/tabular/Images/${path}`;
  }

  // ── Session history ───────────────────────────────────────

  getSessions(): Observable<Session[]> {
    if (this.shouldFail('getSessions')) return throwError(() => ({ message: 'Simulated error: could not load run history' }));
    return this.http.get<{ sessions: Session[] }>('assets/demo/tabular/sessions.json').pipe(
      delay(DEMO_DELAY),
      map(res => res.sessions),
    );
  }

  renameSession(_sessionId: string, _name: string): Observable<{ ok: boolean }> {
    return of({ ok: true });
  }

  deleteSession(_sessionId: string): Observable<void> {
    if (this.shouldFail('deleteSession')) return throwError(() => ({ message: 'Simulated error: could not delete session' }));
    return of(undefined).pipe(delay(DEMO_DELAY));
  }

  finalizeSession(): Observable<{ ok: boolean }> {
    return of({ ok: true }).pipe(delay(DEMO_DELAY));
  }

  cancelSession(): Observable<{ ok: boolean }> {
    if (this.shouldFail('cancelSession')) return throwError(() => ({ message: 'Simulated error: could not cancel the run' }));
    // if (this.failOnce('cancelSession')) return throwError(() => ({ message: 'Simulated error: could not cancel the run' })); // ← uncomment to test individually
    console.log('[Demo] Run cancelled');
    return of({ ok: true }).pipe(delay(DEMO_DELAY));
  }
}
